import { ColumnDef } from '../config/report-configs'
import { MdViewColumn } from 'react-icons/md'

interface ColumnPickerProps {
    /** Todas as colunas do tipo, na ordem canônica. */
    columns: ColumnDef[]
    /** Chaves visíveis. */
    value: string[]
    onChange: (keys: string[]) => void
    disabled?: boolean
}

/**
 * Escolha das colunas visíveis do relatório.
 *
 * Vale para a TELA e para o que sai em PDF, Excel, CSV e e-mail — é a mesma
 * lista de chaves nos dois lados (`COLUMN_DEFS` do backend usa as chaves de
 * `REPORT_CONFIGS`). Sem isso, o operador escondia colunas na tela e o arquivo
 * continuava saindo com as dezessete.
 *
 * Fica num menu, e não em checkboxes soltos, porque a barra já tem quatro botões
 * e os relatórios chegam a dezoito colunas.
 */
export default function ColumnPicker({
    columns,
    value,
    onChange,
    disabled = false,
}: ColumnPickerProps) {
    const total = columns.length
    const selectedCount = value.length

    const toggle = (key: string) => {
        if (value.includes(key)) {
            // A última coluna não pode ser desmarcada: tabela sem coluna nenhuma
            // não mostra nada, e o backend ignoraria a seleção vazia — a tela e o
            // arquivo passariam a divergir sem explicação.
            if (value.length === 1) return
            onChange(value.filter(k => k !== key))
            return
        }
        // Preserva a ordem canônica: marcar de volta não pode jogar a coluna
        // para o fim da tabela.
        onChange(columns.filter(c => c.key === key || value.includes(c.key)).map(c => c.key))
    }

    return (
        <div className="dropdown dropdown-end">
            <button
                tabIndex={0}
                type="button"
                className="btn btn-sm btn-outline gap-2"
                disabled={disabled}
            >
                <MdViewColumn className="text-lg" />
                Colunas
                <span className="badge badge-sm badge-ghost">
                    {selectedCount}/{total}
                </span>
            </button>

            <div
                tabIndex={0}
                className="dropdown-content z-20 mt-1 w-64 rounded-box border border-base-300/60 bg-base-100 shadow-lg"
            >
                <div className="flex items-center justify-between gap-2 border-b border-base-300/60 px-3 py-2">
                    <span className="text-xs font-medium text-base-content/60">
                        Exibir colunas
                    </span>
                    <div className="flex gap-1">
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            onClick={() => onChange(columns.map(c => c.key))}
                            disabled={selectedCount === total}
                        >
                            Todas
                        </button>
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            // Mantém a primeira: "nenhuma" deixaria a tabela vazia.
                            onClick={() => onChange(columns.slice(0, 1).map(c => c.key))}
                            disabled={selectedCount <= 1}
                        >
                            Limpar
                        </button>
                    </div>
                </div>

                <ul className="menu menu-sm max-h-80 flex-nowrap overflow-y-auto p-2">
                    {columns.map(column => {
                        const checked = value.includes(column.key)
                        const isLast = checked && value.length === 1
                        return (
                            <li key={column.key}>
                                <label
                                    className={`flex items-center gap-2 ${
                                        isLast ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                                    }`}
                                    title={
                                        isLast
                                            ? 'Ao menos uma coluna precisa ficar visível'
                                            : undefined
                                    }
                                >
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm checkbox-secondary"
                                        checked={checked}
                                        disabled={isLast}
                                        onChange={() => toggle(column.key)}
                                    />
                                    <span className="truncate">{column.label}</span>
                                </label>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </div>
    )
}
